'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BannerAvatarCardSkeleton } from '@/app/[locale]/vidu-stream/_components/BannerAvatarCardSkeleton';
import { useLiveClaimFlow } from '@/app/[locale]/vidu-stream/_components/use-live-claim-flow';
import { useAvatarDisplayImage } from '@/app/[locale]/vidu-stream/_hooks/use-avatar-display-image';
import { listPresetAvatars, listUserAvatars } from '@/app/[locale]/vidu-stream/apis';
import { getBannerAvatarCardImage } from '@/app/[locale]/vidu-stream/utils';
import { Loading } from '@/app/components/icons/Loading';
import type { LiveAvatar } from '@/app/types';
import { cn } from '@/app/utils';

const S1_SKELETON_COUNT = 7;
let s1RolesCache: { ugcRoles: LiveAvatar[]; pgcRoles: LiveAvatar[] } | null = null;

interface HomeS1RoleCardProps {
  role: LiveAvatar;
  isClaiming: boolean;
  onSelect: (avatarId: string, role: LiveAvatar) => void;
}

const HomeS1RoleCard = ({ role, isClaiming, onSelect }: HomeS1RoleCardProps) => {
  const t = useTranslations();
  const displayImage = useAvatarDisplayImage(getBannerAvatarCardImage(role));
  const roleName = role.name ?? '—';
  const avatarId = role.id?.trim() ?? '';

  return (
    <button
      type="button"
      onClick={() => onSelect(avatarId, role)}
      disabled={isClaiming}
      className="group flex w-[158px] shrink-0 flex-col gap-2 rounded-16 bg-white/[0.08] px-1 pt-1 pb-3 text-center backdrop-blur-[20px] transition-colors duration-300 hover:bg-white/[0.28] disabled:cursor-not-allowed"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-8 bg-[rgba(44,44,45,0.70)]">
        {role.avatar_type === 'user' ? (
          <span className="absolute top-2 left-2 z-10 rounded-4 bg-black/50 px-1.5 py-0.5 font-medium text-[10px] text-white backdrop-blur-sm">
            {t('home.hero.myRoleTag')}
          </span>
        ) : null}
        {displayImage ? (
          <img
            src={displayImage}
            alt={roleName}
            loading="lazy"
            className="pointer-events-none absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : null}
        {isClaiming ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Loading className="animate-spin text-2xl text-white" />
          </div>
        ) : null}
      </div>
      <span className="block max-w-full truncate font-medium text-sm text-system-white sm:text-xs">{roleName}</span>
    </button>
  );
};

interface HomeS1RolesProps {
  isActive: boolean;
  onHeightChange: (height: number) => void;
}

export function HomeS1Roles({ isActive, onHeightChange }: HomeS1RolesProps) {
  const t = useTranslations();
  const rolesBlockRef = useRef<HTMLDivElement>(null);
  const [roles, setRoles] = useState(() => s1RolesCache ?? { ugcRoles: [], pgcRoles: [] });
  const [isLoading, setIsLoading] = useState(() => s1RolesCache === null);
  const skeletonKeys = Array.from({ length: S1_SKELETON_COUNT }, (_, i) => `s1-skeleton-${i + 1}`);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const [ugcRes, pgcRes] = await Promise.all([listUserAvatars(), listPresetAvatars()]);
      const nextRoles = {
        ugcRoles: ugcRes.error ? [] : (ugcRes.data?.roles ?? []),
        pgcRoles: pgcRes.error ? [] : (pgcRes.data?.roles ?? []),
      };
      s1RolesCache = nextRoles;
      setRoles(nextRoles);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 登录后 UGC 才拿得到，重新拉一次让「我的角色」出现。
  const handleLoggedIn = useCallback(() => {
    s1RolesCache = null;
    void fetchRoles();
  }, [fetchRoles]);

  const { claimingAvatarId, startLiveCall, modals } = useLiveClaimFlow({
    origin: 'home',
    onLoggedIn: handleLoggedIn,
  });

  const handleSelectRole = useCallback(
    // 埋点在 startLiveCall 内统一上报（含 click_position），此处不再重复。
    (avatarId: string, role: LiveAvatar) => {
      startLiveCall(avatarId, role, 'home - s1-role');
    },
    [startLiveCall],
  );

  useEffect(() => {
    if (s1RolesCache !== null) return;
    void fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const el = rolesBlockRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => onHeightChange(el.offsetHeight));
    ro.observe(el);
    onHeightChange(el.offsetHeight);
    return () => ro.disconnect();
  }, [onHeightChange]);

  const displayRoles = [...roles.ugcRoles, ...roles.pgcRoles];

  return (
    <div
      ref={rolesBlockRef}
      className={cn(
        'col-start-1 row-start-1 ml-[-23.53%] flex w-[147.06%] min-w-0 flex-col gap-3 self-start pt-6 pb-10 transition-all duration-300',
        isActive ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
      )}
      aria-hidden={!isActive}
    >
      <h3 className="font-medium text-sm text-white sm:text-xs">{t('home.hero.s1CardsTitle')}</h3>
      <div className="hide-scrollbar flex min-w-0 flex-nowrap items-end justify-start gap-4 overflow-x-auto">
        {isLoading
          ? skeletonKeys.map((key) => <BannerAvatarCardSkeleton key={key} />)
          : displayRoles.map((role) => {
              const avatarId = role.id?.trim() ?? '';
              if (!avatarId) return null;
              return (
                <HomeS1RoleCard
                  key={avatarId}
                  role={role}
                  isClaiming={claimingAvatarId === avatarId}
                  onSelect={handleSelectRole}
                />
              );
            })}
      </div>
      {modals}
    </div>
  );
}
